import React from 'react';
import { AlertCircle, Play } from 'lucide-react';

interface MediaItem {
  src: string;
  alt: string;
}

interface CarouselPreviewProps {
  mediaItems: MediaItem[];
  className?: string;
  imageSize?: 'default' | 'small';
}

// Helper function to detect if a URL is a video file
const isVideoFile = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv'];
  const urlPath = url.toLowerCase().split('?')[0]; // Remove query parameters
  return videoExtensions.some(ext => urlPath.endsWith(ext));
};

const MediaThumbnail: React.FC<{
  item: MediaItem;
  size: number;
  maxItems: number;
  index: number;
  totalItems: number;
}> = ({ item, size, maxItems, index, totalItems }) => {
  const [hasError, setHasError] = React.useState(false);
  const isVideo = isVideoFile(item.src);
  const showOverflow = index === maxItems - 1 && totalItems > maxItems;
  const overflowCount = totalItems - maxItems + 1;

  const handleError = () => {
    setHasError(true);
  };

  if (hasError) {
    return (
      <div
        className="bg-gray-100 border border-gray-200  flex items-center justify-center relative"
        style={{ width: size, height: size }}
        title={item.alt || 'Failed to load media'}
      >
        <AlertCircle className="h-4 w-4 text-gray-400" />
        {showOverflow && (
          <div className="absolute inset-0 bg-black bg-opacity-60  flex items-center justify-center">
            <span className="text-white text-xs font-medium">+{overflowCount}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {isVideo ? (
        <video
          src={item.src}
          className="w-full h-full object-cover  border border-gray-200"
          onError={handleError}
          preload="metadata"
          muted
        />
      ) : (
        <img
          src={item.src}
          alt={item.alt}
          className="w-full h-full object-cover  border border-gray-200"
          onError={handleError}
        />
      )}

      {/* Video play icon overlay */}
      {isVideo && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black bg-opacity-50 rounded-full p-1">
            <Play className="h-3 w-3 text-white fill-current" />
          </div>
        </div>
      )}

      {/* Overflow count overlay */}
      {showOverflow && (
        <div className="absolute inset-0 bg-black bg-opacity-60  flex items-center justify-center">
          <span className="text-white text-xs font-medium">+{overflowCount}</span>
        </div>
      )}
    </div>
  );
};

const CarouselPreview: React.FC<CarouselPreviewProps> = ({
  mediaItems,
  className = '',
  imageSize = 'default'
}) => {
  // Determine thumbnail size based on imageSize prop
  const thumbnailSize = imageSize === 'small' ? 48 : 60;
  const maxDisplayItems = 12; // Show up to 12 items before overflow indicator
  const displayItems = mediaItems.slice(0, maxDisplayItems);

  if (mediaItems.length === 0) {
    return null;
  }

  return (
    <div className={`bg-indigo-50 border border-indigo-200  p-3 ${className}`}>
      {/* Header */}
      <div className="mb-3">
        <h4 className="text-sm font-medium text-gray-900">
          Carousel ({mediaItems.length} item{mediaItems.length !== 1 ? 's' : ''})
        </h4>
      </div>

      {/* Horizontal Thumbnail Layout */}
      <div className="flex flex-wrap gap-2">
        {displayItems.map((item, index) => (
          <MediaThumbnail
            key={index}
            item={item}
            size={thumbnailSize}
            maxItems={maxDisplayItems}
            index={index}
            totalItems={mediaItems.length}
          />
        ))}
      </div>

      {/* Additional info for large carousels */}
      {mediaItems.length > maxDisplayItems && (
        <div className="mt-2 text-xs text-gray-500">
          Showing {maxDisplayItems} of {mediaItems.length} items
        </div>
      )}
    </div>
  );
};

export default CarouselPreview;
